import {
  Button,
  Divider,
  HStack,
  Icon,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Text,
  useDisclosure,
  VStack,
} from '@elkaso-app/web-design';
import { CmsCatalogCategoryDto, useGetCmsCatalogCategoriesApi } from 'apis/cms-catalog-categories';
import { usePageParams } from 'hooks/use-page-params';
import { useState } from 'react';
import { BiChevronDown, BiChevronLeft, BiChevronRight } from 'react-icons/bi';

// Category tree is 5 levels (depth 0-4); nodes at max depth have no children to drill into.
const MAX_DEPTH = 4;

// Drill-down picker over GET /cms/catalog-categories. Selecting any node sets
// ?category=<id> (BE filters by exact catalogCategoryId match). The selected
// title only lives in state — after a full reload the trigger falls back to the id.
export const CategoryFilterButton = () => {
  const { getPageParams, setPageParams } = usePageParams();
  const params = getPageParams();
  const categoryId = params.category ? Number(params.category) : undefined;

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [path, setPath] = useState<CmsCatalogCategoryDto[]>([]);
  const [selectedTitle, setSelectedTitle] = useState<string>();

  const parent = path[path.length - 1];
  const { data, isLoading } = useGetCmsCatalogCategoriesApi(parent ? { parentId: parent.id } : {}, {
    enabled: isOpen,
  });
  const categories = data?.data ?? [];

  const apply = (category?: CmsCatalogCategoryDto) => {
    const next = { ...params };
    delete next.page;
    if (category) next.category = category.id;
    else delete next.category;
    setPageParams(next);
    setSelectedTitle(category?.title ?? undefined);
    onClose();
  };

  const label = categoryId ? selectedTitle ?? `#${categoryId}` : 'All';

  return (
    <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement='bottom-start'>
      <PopoverTrigger>
        <Button
          size='sm'
          variant='outline'
          fontWeight='semibold'
          color='gray.600'
          rightIcon={<Icon as={BiChevronDown} />}>
          Category: {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent w='280px'>
        <PopoverBody p='sm' maxH='340px' overflowY='auto'>
          <VStack align='stretch' spacing='2px'>
            {path.length > 0 ? (
              <Button
                size='sm'
                variant='ghost'
                justifyContent='flex-start'
                leftIcon={<Icon as={BiChevronLeft} />}
                onClick={() => setPath((prev) => prev.slice(0, -1))}>
                {parent?.title ?? 'Back'}
              </Button>
            ) : (
              <Button size='sm' variant='ghost' justifyContent='flex-start' onClick={() => apply(undefined)}>
                All categories
              </Button>
            )}

            <Divider />

            {isLoading && (
              <HStack justify='center' py='md'>
                <Spinner size='sm' />
              </HStack>
            )}

            {!isLoading && !categories.length && (
              <Text fontSize='sm' color='gray.500' textAlign='center' py='md'>
                No sub-categories
              </Text>
            )}

            {categories.map((category) => (
              <HStack key={category.id} spacing='2px'>
                <Button
                  size='sm'
                  variant='ghost'
                  justifyContent='flex-start'
                  flex={1}
                  fontWeight={category.id === categoryId ? 'bold' : 'normal'}
                  onClick={() => apply(category)}>
                  {category.title ?? `#${category.id}`}
                </Button>
                {category.depth < MAX_DEPTH && (
                  <IconButton
                    aria-label='Drill into category'
                    icon={<Icon as={BiChevronRight} />}
                    size='sm'
                    variant='ghost'
                    onClick={() => setPath((prev) => [...prev, category])}
                  />
                )}
              </HStack>
            ))}
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
